import { differenceInDays, differenceInHours, differenceInMinutes, differenceInSeconds } from 'date-fns'
import { useCallback, useEffect, useState } from 'react'
import { useInterval } from 'react-use'

// ****************************************************************
// ****************************************************************
// ****************************************************************
// What time is it? It's Hackventure Time!
// We need to figure out if the user is in summer- or winter time,
// and javascript natives won't tell us. So this neat little extension
// comes courtesy of https://stackoverflow.com/a/11888430, and supposedly
// fixes everything right up.

declare global {
  interface Date {
    isDSTActive: () => boolean
    stdTimezoneOffset: () => number
  }
}

// I know, I know, don't extend native prototypes - but as you can see, I'm only doing it if the
// function doesn't exist already, which should make accidental overrides very unlikely.
// eslint-disable-next-line no-extend-native
Date.prototype.stdTimezoneOffset = Date.prototype.stdTimezoneOffset || function stdTimezoneOffset() {
  const jan = new Date((this as unknown as Date).getFullYear(), 0, 1)
  const jul = new Date((this as unknown as Date).getFullYear(), 6, 1)
  return Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset())
}

// See above.
// eslint-disable-next-line no-extend-native
Date.prototype.isDSTActive = Date.prototype.isDSTActive
  || function isDSTActive() { return (this as unknown as Date).getTimezoneOffset() < (this as unknown as Date).stdTimezoneOffset() }

// Hacky time ends here. Thanks for joining us!
// ****************************************************************
// ****************************************************************
// ****************************************************************

interface UrgencyTimerOptions {
  endAt: Date
  startAt: Date
}

type UrgencyTimerReturnFormat = {
  days: number
  deadlineReached: boolean
  dstIsActive: boolean
  hours: number
  minutes: number
  percentage: number
  referenceDate: Date
  seconds: number
  secondsInTotal: number
}

export const useUrgencyTimer = ({
  endAt,
  startAt,
}: UrgencyTimerOptions): UrgencyTimerReturnFormat => {
  const calculateDeltas = useCallback((): UrgencyTimerReturnFormat => {
    const rightNowInDenmark = new Date((new Date()).toLocaleString('en-US', { timeZone: 'Europe/Copenhagen' }))

    const deadlineReached = rightNowInDenmark > endAt
    const deltaDays = differenceInDays(endAt, rightNowInDenmark)
    const deltaHoursInTotal = differenceInHours(endAt, rightNowInDenmark)
    const deltaHours = deltaHoursInTotal % 24
    const deltaMinutesInTotal = differenceInMinutes(endAt, rightNowInDenmark)
    const deltaMinutes = deltaMinutesInTotal % 60
    const deltaSecondsInTotal = differenceInSeconds(endAt, rightNowInDenmark)
    const deltaSeconds = deltaSecondsInTotal % 60
    const dstIsActive = rightNowInDenmark.isDSTActive()
    const totalDifferenceInSeconds = differenceInSeconds(endAt, startAt)
    let percentageLeft = ((deltaSecondsInTotal / totalDifferenceInSeconds) * 100) / 2

    if (deadlineReached) {
      percentageLeft = 0
    }

    return {
      days: deltaDays,
      deadlineReached,
      dstIsActive,
      hours: deltaHours,
      minutes: deltaMinutes,
      percentage: percentageLeft,
      referenceDate: rightNowInDenmark,
      seconds: deltaSeconds,
      secondsInTotal: deltaSecondsInTotal,
    }
  }, [endAt, startAt])

  const [timerData, setTimerData] = useState({
    days: 0,
    deadlineReached: false,
    dstIsActive: false,
    hours: 0,
    minutes: 0,
    percentage: 0,
    referenceDate: new Date(),
    seconds: 0,
    secondsInTotal: 0,
  })

  const updateTimers = useCallback(() => {
    setTimerData(calculateDeltas())
  }, [calculateDeltas])

  useInterval(updateTimers, timerData.deadlineReached ? null : 1000)

  useEffect(() => updateTimers(), [updateTimers])

  return timerData
}

